import BaseOption from "%/dynamic-component-editor/classes/base-option";
import { PerformanceTimer } from "@vcl";

// Lazy store resolution: a module-scope import here closes a cycle
// (stores -> components -> mixins -> classes). See scripts/find-import-cycles.py.
// eslint-disable-next-line global-require
const useConfigurationStore = (...args) => require("%/dynamic-component-editor/stores/configuration-store").useConfigurationStore(...args);
// eslint-disable-next-line global-require
const useDynamicComponentEditorStore = (...args) => require("%/dynamic-component-editor/stores/dynamic-component-editor").default(...args);

const INHERITANCE_TIMING_GROUP = "inheritance-calculation";

export class InheritanceHelper {

  // lookup values from compiled configs
  static getInheritedOptionValue(option, state, config) {
    const timer = PerformanceTimer.startTimer(
      "InheritanceHelper.getInheritedOptionValue",
      INHERITANCE_TIMING_GROUP,
    );

    let optionKey = option;
    if (typeof option !== "string") {
      optionKey = option.optionKey;
    }

    // Destination-scoped, not useConfigurationStore's publication-wide list —
    // those two can diverge (a Studio-created breakpoint isn't mirrored into PublicationConfig).
    const allBreakpoints = useDynamicComponentEditorStore().availableBreakpoints;
    const [statePart, breakpointPart] = state.split(":");
    let currentBreakpoint;
    let currentBreakpointValue;

    if (breakpointPart) {
      currentBreakpoint = allBreakpoints.find(bp => bp.alias === breakpointPart);
      if (!currentBreakpoint) {
        // eslint-disable-next-line no-console
        console.warn(
          `[inheritance-helper] active breakpoint "${breakpointPart}" not found among the destination's breakpoints — falling back to default`,
          { optionKey },
        );
        PerformanceTimer.stopTimer(timer);
        return config[`default.${optionKey}`];
      }
      currentBreakpointValue = parseInt(currentBreakpoint.value);
    }

    let value = config[`${state}.${optionKey}`];

    if (value === undefined && state.includes(":")) {
      value = config[`${BaseOption.$states.DEFAULT}:${state.split(":")[1]}.${optionKey}`];

      if (value === undefined) {
        const availableBreakpoints = allBreakpoints.map(bp => {
          return { ...bp, value: parseInt(bp.value.replace("px", "")) };
        }).filter(bp => {
          if (useConfigurationStore().designVariant !== "mobile") {
            return bp.value !== null && bp.value > currentBreakpointValue;
          }
          return bp.value !== null && bp.value < currentBreakpointValue;
        }).sort((a, b) => a.value - b.value); // Sort ascending

        for (const breakpoint of availableBreakpoints) {
          const breakpointKey = `${statePart}:${breakpoint.alias}.${optionKey}`;
          const defaultBreakpointKey = `default:${breakpoint.alias}.${optionKey}`;
          if (config[breakpointKey] !== undefined || config[defaultBreakpointKey] !== undefined) {
            value = config[breakpointKey] ?? config[defaultBreakpointKey];
            break;
          }
        }
      }
    }

    if (value === undefined) {
      if (state.startsWith("hover")) {
        value = config[`hover.${optionKey}`];
      }

      if (value === undefined) {
        value = config[`default.${optionKey}`];
      }
    }

    PerformanceTimer.stopTimer(timer);
    return value;
  }
}
